import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCopilot } from '../contexts/CopilotContext';
import MillosLogo from './MillosLogo';
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  useTheme,
} from '@mui/material';
import {
  CalendarToday,
  Warning,
  Error,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule,
  AccessTime,
  Email as EmailIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import ReminderEditDialog from './ReminderEditDialog';
import EmailDialog from './EmailDialog';
import ExpiryRing from './ExpiryRing';
import { getCategoryColor } from '../theme';
import config from '../config';

interface Certificate {
  id: string;
  certificate_type: string;
  certificate_type_category?: string;
  issue_date: string;
  expiry_date: string;
  property_address: string;
  street_address?: string;
  contractor_name?: string;
  original_filename?: string;
  pdf_filename?: string;
  normalized_address?: string;
  inspector_name?: string;
  certificate_number?: string;
  notes?: string;
  created_at?: string;
  last_email_sent_at?: string;
  last_email_sent_to?: string;
}

interface ReminderStatus {
  has_reminders: boolean;
  total_active_reminders: number;
  active_reminders: Array<{
    id: string;
    reminder_days_before: number;
    reminder_date: string;
    status: string;
  }>;
  reminders?: Array<{
    id: string;
    reminder_days_before: number;
    reminder_date: string;
    status: string;
  }>;
}

interface CertificateListRowProps {
  certificate: Certificate;
  onEdit?: (certificate: Certificate) => void;
  onDelete?: (certificateId: string) => void;
  onView?: (certificateId: string) => void;
  onReminderSuccess?: (message: string) => void;
  onReminderError?: (error: string) => void;
  reminderRefreshTrigger?: number;
  isNewlyAdded?: boolean;
  isAutoExpanded?: boolean;
  isDimmed?: boolean;
  onFieldUpdate?: () => void;
  enlarged?: boolean;
}

const CATEGORY_OPTIONS = [
  'Gas Certificate',
  'Insurance Certificate',
  'Licence Certificate',
  'Electrical Certificate',
  'Fire Certificate',
  'Smoke Certificate',
  'Deposit Protection Certificate',
  'Energy Certificate',
  'Other',
];


// Single full-width certificate row (thumbnail, title/filename, right-aligned dates) — the
// tabbed-units property view shows one unit's certs at a time in this format instead of the
// tile-grid EnhancedCertificateCard, which is still used elsewhere for grid-style listings.
const CertificateListRow: React.FC<CertificateListRowProps> = ({
  certificate,
  onEdit,
  onDelete,
  onView,
  onReminderSuccess,
  onReminderError,
  reminderRefreshTrigger = 0,
  isNewlyAdded = false,
  isAutoExpanded = false,
  isDimmed = false,
  onFieldUpdate,
  enlarged = false,
}) => {
  const { showMessage } = useCopilot();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const [reminderStatus, setReminderStatus] = useState<ReminderStatus | null>(null);
  const [reminderMenuAnchor, setReminderMenuAnchor] = useState<null | HTMLElement>(null);
  const [isCreatingReminder, setIsCreatingReminder] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);

  // Inline-editable fields (title, category, issue/expiry date) — mirrors the `certificate` prop
  // but can be updated optimistically the instant a click-to-edit field is saved, without waiting
  // on the parent's (silent) property-tree refetch. Notes/inspector/certificate-number stay behind
  // the Edit dialog since they're not shown on this row at all.
  const [localCert, setLocalCert] = useState(certificate);
  useEffect(() => { setLocalCert(certificate); }, [certificate]);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(certificate.certificate_type);
  useEffect(() => { setTitleDraft(localCert.certificate_type); }, [localCert.certificate_type]);

  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<null | HTMLElement>(null);

  const [editingDateField, setEditingDateField] = useState<'issue_date' | 'expiry_date' | null>(null);
  const [dateDraft, setDateDraft] = useState('');

  const saveField = async (field: keyof Certificate, value: string) => {
    if (value === (localCert as any)[field]) return;
    const original = localCert;
    setLocalCert(prev => ({ ...prev, [field]: value }));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new window.Error('update failed');
      onFieldUpdate?.();
    } catch (error) {
      setLocalCert(original);
      onReminderError?.('Failed to save change. Please try again.');
    }
  };

  const commitTitleEdit = () => {
    const trimmed = titleDraft.trim();
    setEditingTitle(false);
    if (!trimmed) { setTitleDraft(localCert.certificate_type); return; }
    saveField('certificate_type', trimmed);
  };

  const startEditDate = (field: 'issue_date' | 'expiry_date') => {
    setEditingDateField(field);
    setDateDraft(localCert[field] || '');
  };

  const commitDateEdit = () => {
    if (!editingDateField) return;
    const field = editingDateField;
    setEditingDateField(null);
    if (!dateDraft) return;
    saveField(field, dateDraft);
  };

  const getCertificateStatus = () => {
    const today = new Date();
    const expiryDate = new Date(localCert.expiry_date);
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring';
    return 'valid';
  };

  const certStatus = getCertificateStatus();
  const showGreenHighlight = isNewlyAdded && !hasBeenClicked;
  const showRedHighlight = isAutoExpanded && !hasBeenClicked && certStatus === 'expired';
  const showYellowHighlight = isAutoExpanded && !hasBeenClicked && certStatus === 'expiring';

  useEffect(() => {
    if (isAutoExpanded) setHasBeenClicked(false);
  }, [isAutoExpanded]);

  useEffect(() => {
    if (showGreenHighlight || showRedHighlight || showYellowHighlight) {
      const timer = setTimeout(() => setHasBeenClicked(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [showGreenHighlight, showRedHighlight, showYellowHighlight]);

  useEffect(() => {
    loadReminderStatus();
  }, [certificate.id, reminderRefreshTrigger]);

  const loadReminderStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const response = await fetch(`${config.apiUrl}/api/reminders/certificate/${certificate.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setReminderStatus(data.reminder_status);
      }
    } catch (err) {
      console.error('Error loading reminder status:', err);
    }
  };

  const createReminder = async (days: number) => {
    setIsCreatingReminder(true);
    setReminderMenuAnchor(null);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        onReminderError?.('Please log in first');
        return;
      }
      const response = await fetch(`${config.apiUrl}/api/reminders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ certificate_id: certificate.id, reminder_days: days }),
      });
      const data = await response.json();
      if (data.success) {
        await loadReminderStatus();
        onReminderSuccess?.(`✅ Reminder created: ${days} days before expiry`);
      } else {
        onReminderError?.(data.error || 'Failed to create reminder');
      }
    } catch (err) {
      onReminderError?.((err as Error).message || 'Failed to create reminder');
    } finally {
      setIsCreatingReminder(false);
    }
  };

  const getDaysUntilExpiry = () => {
    try {
      if (!localCert.expiry_date ||
          localCert.expiry_date === 'N/A' ||
          localCert.expiry_date.toLowerCase().includes('not_found') ||
          localCert.expiry_date.toLowerCase().includes('expiry_date_not_found')) {
        return null;
      }
      const expiryDate = new Date(localCert.expiry_date);
      if (isNaN(expiryDate.getTime())) return null;
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const getExpiryStatus = () => {
    const daysUntilExpiry = getDaysUntilExpiry();
    if (daysUntilExpiry === null) return { color: 'warning', label: 'No Date Found', severity: 'warning' };
    if (daysUntilExpiry < 0) return { color: 'error', label: `Expired ${Math.abs(daysUntilExpiry)} days ago`, severity: 'error' };
    if (daysUntilExpiry === 0) return { color: 'error', label: 'Expires today', severity: 'error' };
    if (daysUntilExpiry <= 30) return { color: 'warning', label: `${daysUntilExpiry} days left`, severity: 'warning' };
    if (daysUntilExpiry <= 90) return { color: 'info', label: `${daysUntilExpiry} days left`, severity: 'info' };
    return { color: 'success', label: `${daysUntilExpiry} days left`, severity: 'success' };
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const expiryStatus = getExpiryStatus();
  const hasActiveReminders = reminderStatus?.has_reminders &&
    (reminderStatus.active_reminders?.length > 0 || reminderStatus.reminders?.some(r => r.status === 'active'));

  const handleReminderClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    await loadReminderStatus();
    setReminderDialogOpen(true);
  };

  const handleAssistantClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const openAssistant = { label: 'Open AI assistant', onClick: () => navigate('/copilot') };

    if (hasActiveReminders) {
      showMessage({
        text: 'A flow is already running for this certificate. You can follow it in AI assistant.',
        action: openAssistant,
        secondaryAction: { label: 'Edit reminder', onClick: async () => { await loadReminderStatus(); setReminderDialogOpen(true); } },
      });
      return;
    }
    if (localStorage.getItem('agenticMode') !== 'true') {
      showMessage({ text: 'The AI assistant is switched off. Switch it on in AI assistant and I will start a flow for this certificate.', action: openAssistant });
      return;
    }

    setIsCreatingReminder(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/start-flow`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        showMessage({ text: result.error || 'I could not start a flow for this certificate.' });
        return;
      }
      await loadReminderStatus();
      const active = result.contacts_enabled || result.calendar_enabled;
      showMessage({
        text: active
          ? 'I am starting a flow for this certificate. You can follow it in AI assistant.'
          : 'I created a flow for this certificate, but calendar reminders and contacting providers and tenants are both switched off in Personalize. You can follow it in AI assistant.',
        action: openAssistant,
      });
    } catch {
      showMessage({ text: 'I could not start a flow for this certificate.' });
    } finally {
      setIsCreatingReminder(false);
    }
  };

  const handleReminderSave = async (reminderText: string, daysBeforeExpiry: number, reminderTime: string) => {
    try {
      setIsCreatingReminder(true);
      const token = localStorage.getItem('auth_token');
      const existingReminder = reminderStatus?.active_reminders?.[0];
      let response: Response;
      if (existingReminder) {
        const expiryDate = new Date(localCert.expiry_date);
        expiryDate.setDate(expiryDate.getDate() - daysBeforeExpiry);
        const reminderDate = expiryDate.toISOString().split('T')[0];
        response = await fetch(`${config.apiUrl}/api/reminders/${existingReminder.id}/update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ reminder_date: reminderDate, reminder_days_before: daysBeforeExpiry, reminder_time: reminderTime }),
        });
      } else {
        response = await fetch(`${config.apiUrl}/api/reminders/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ certificate_id: certificate.id, reminder_days: daysBeforeExpiry, reminder_time: reminderTime }),
        });
      }
      const result = await response.json();
      if (response.ok && result.success) {
        await loadReminderStatus();
        onReminderSuccess?.(`Reminder set for ${daysBeforeExpiry} ${daysBeforeExpiry === 1 ? 'day' : 'days'} before expiry`);
      } else {
        let errorMessage = 'Failed to save reminder. Please try again.';
        if (result.code === 'AUTH_REQUIRED') errorMessage = result.error || errorMessage;
        else if (result.code === 'CERT_EXPIRED') errorMessage = 'The certificate has already expired. Reminders can only be set for active certificates.';
        else if (result.code === 'DUPLICATE_REMINDER') errorMessage = `A reminder for ${daysBeforeExpiry} days before expiry already exists for this certificate.`;
        else if (result.code === 'INVALID_DATE') errorMessage = 'The certificate has an invalid expiry date format.';
        else if (result.error) errorMessage = result.error;
        throw new window.Error(errorMessage);
      }
    } catch (error) {
      onReminderError?.((error as Error)?.message || 'Failed to save reminder. Please try again.');
    } finally {
      setIsCreatingReminder(false);
    }
  };

  const handleDeleteReminder = () => {
    const firstReminder = reminderStatus?.active_reminders?.[0];
    if (!firstReminder) {
      showMessage({ text: 'No active reminder found to delete.' });
      return;
    }
    showMessage({
      text: 'Delete this reminder? This will also remove the Google Calendar event.',
      confirm: {
        label: 'Yes, delete',
        onConfirm: async () => {
          try {
            const token = localStorage.getItem('auth_token');
            if (!token) { showMessage({ text: 'Authentication required.' }); return; }
            const response = await fetch(`${config.apiUrl}/api/reminders/${firstReminder.id}/delete`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const result = await response.json().catch(() => ({ success: false, error: 'Invalid response' }));
            if (response.ok && result.success) {
              await loadReminderStatus();
              setReminderDialogOpen(false);
              onReminderSuccess?.('Reminder deleted successfully');
            } else {
              showMessage({ text: result.error || result.message || 'Failed to delete reminder.' });
            }
          } catch (error) {
            showMessage({ text: (error as Error)?.message || 'Failed to delete reminder. Please try again.' });
          }
        },
      },
    });
  };

  const reminderOptions = [
    { days: 30, label: '30 days before', icon: <Schedule /> },
    { days: 14, label: '14 days before', icon: <CalendarToday /> },
    { days: 7, label: '7 days before', icon: <AccessTime /> },
    { days: 3, label: '3 days before', icon: <Warning /> },
    { days: 1, label: '1 day before', icon: <Error /> },
  ];

  const handleOpenVersionHistory = async () => {
    setVersionDialogOpen(true);
    setLoadingVersions(true);
    setVersionError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/versions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new window.Error('Failed to load version history');
      const data = await response.json();
      setVersions(data.versions || []);
    } catch (error: any) {
      setVersionError(error.message || 'Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRevertToVersion = (versionId: string) => {
    showMessage({
      text: 'Revert to this version? The current certificate data will be replaced.',
      confirm: {
        label: 'Yes, revert',
        onConfirm: async () => {
          try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/revert/${versionId}`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new window.Error('Failed to revert to version');
            onReminderSuccess?.('Certificate reverted successfully');
            setVersionDialogOpen(false);
            window.location.reload();
          } catch (error: any) {
            showMessage({ text: error.message || 'Failed to revert version. Please try again.' });
          }
        },
      },
    });
  };

  const highlightSx = showGreenHighlight ? {
    animation: 'pulse-green-row 5s ease-in-out 1',
    border: '2px solid rgba(76, 175, 80, 0.8)',
    boxShadow: '0 0 15px 5px rgba(76, 175, 80, 0.4)',
    '@keyframes pulse-green-row': {
      '0%, 100%': { boxShadow: '0 0 15px 5px rgba(76, 175, 80, 0.4)' },
      '50%': { boxShadow: '0 0 20px 10px rgba(76, 175, 80, 0.6)' },
    },
  } : showRedHighlight ? {
    // Single fade-in, not a repeating pulse — a continuous alarm-style pulse read as
    // "something's wrong" rather than "look here" for a cert you already know is expired.
    animation: 'fade-in-highlight-row 0.5s ease-out',
    borderLeft: `4px solid ${palette.tokens.error}`,
    bgcolor: 'rgba(198, 69, 69, 0.06)',
    '@keyframes fade-in-highlight-row': {
      '0%': { opacity: 0 },
      '100%': { opacity: 1 },
    },
  } : showYellowHighlight ? {
    animation: 'fade-in-highlight-row 0.5s ease-out',
    borderLeft: `4px solid ${palette.tokens.warning}`,
    bgcolor: 'rgba(212, 160, 23, 0.08)',
    '@keyframes fade-in-highlight-row': {
      '0%': { opacity: 0 },
      '100%': { opacity: 1 },
    },
  } : {};

  return (
    <Box
      onClick={() => showGreenHighlight && setHasBeenClicked(true)}
      // Marks this row so the page-level click-outside listener (which clears the
      // expired/expiring filter) can tell "clicked a highlighted cert" apart from
      // "clicked away" — clicking the highlighted content itself shouldn't dismiss it.
      data-cert-highlight={isAutoExpanded ? 'true' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: enlarged ? 2.5 : 1.5,
        mb: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: palette.tokens.white,
        position: 'relative',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        opacity: isDimmed ? 0.25 : 1,
        filter: isDimmed ? 'grayscale(60%)' : 'none',
        pointerEvents: isDimmed ? 'none' : 'auto',
        transition: 'opacity 0.4s ease, filter 0.4s ease, padding 0.25s ease',
        cursor: (showGreenHighlight || showRedHighlight || showYellowHighlight) ? 'pointer' : 'default',
        ...highlightSx,
      }}
    >
      {/* Thumbnail — 30% larger when enlarged, mainly so it actually reads as an image/icon
          rather than a sliver next to the text. */}
      <Box
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (onView) onView(certificate.id); }}
        sx={{
          width: enlarged ? 95 : 56, height: enlarged ? 122 : 72, flexShrink: 0,
          transition: 'width 0.25s ease, height 0.25s ease',
          border: '1px solid', borderColor: 'divider', borderRadius: 1,
          overflow: 'hidden', cursor: onView ? 'pointer' : 'default',
          '&:hover': onView ? { opacity: 0.8, borderColor: 'primary.main' } : {},
        }}
      >
        <img
          src={`${config.apiUrl}/api/certificate-preview/${certificate.id}?t=${encodeURIComponent(certificate.pdf_filename || certificate.created_at || Date.now().toString())}`}
          alt={`${localCert.certificate_type} preview`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            const parent = (e.target as HTMLElement).parentElement;
            if (parent && !parent.querySelector('.pdf-fallback')) {
              const fallback = document.createElement('div');
              fallback.className = 'pdf-fallback';
              fallback.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;background:#f5f5f5;color:#666;';
              fallback.innerHTML = '<div style="text-align:center"><div style="font-size:16px">📄</div></div>';
              parent.appendChild(fallback);
            }
          }}
        />
      </Box>

      {/* Title + filename + category chip — title and category are click-to-edit inline;
          notes/inspector/certificate-number stay behind the Edit dialog since they're not
          shown on this row. */}
      <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 160 }, order: { xs: 1, md: 0 } }}>
        {editingTitle ? (
          <TextField
            autoFocus
            size="small"
            variant="standard"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitleEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTitleEdit();
              if (e.key === 'Escape') { setTitleDraft(localCert.certificate_type); setEditingTitle(false); }
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ width: '100%', '& .MuiInputBase-input': { fontWeight: 700, fontSize: '0.95rem', p: 0 } }}
          />
        ) : (
          <Typography
            onClick={(e) => { e.stopPropagation(); setEditingTitle(true); }}
            sx={{
              fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3, wordBreak: 'break-word',
              cursor: 'pointer', display: 'inline-block',
              '&:hover': { textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' },
            }}
          >
            {localCert.certificate_type}
          </Typography>
        )}

        {certificate.original_filename && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.66rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {certificate.original_filename}
          </Typography>
        )}

        {/* Category chip + action icons, side by side — per the layout redesign (right side is
            now just dates + the ring, no separate icon column eating horizontal space). */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Chip
            label={(localCert.certificate_type_category || 'Other').replace(' Certificate', '')}
            size="small"
            onClick={(e) => { e.stopPropagation(); setCategoryMenuAnchor(e.currentTarget); }}
            sx={{
              height: '18px', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
              backgroundColor: getCategoryColor(localCert.certificate_type_category || 'Other'), color: 'white',
              '& .MuiChip-label': { px: 0.75, py: 0 },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {onEdit && (
              <Tooltip title="Edit certificate details">
                <IconButton onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(certificate); }} size="small">
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View version history">
              <IconButton onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenVersionHistory(); }} size="small">
                <HistoryIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={certificate.last_email_sent_at ? `Email sent ${new Date(certificate.last_email_sent_at).toLocaleDateString()} to ${certificate.last_email_sent_to}` : 'Send inspection request email'}>
              <span>
                <IconButton onClick={(e) => { e.stopPropagation(); setEmailDialogOpen(true); }} size="small" sx={{ color: certificate.last_email_sent_at ? 'success.main' : 'info.main' }}>
                  <EmailIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={hasActiveReminders ? 'AI assistant flow running' : 'Start AI assistant flow'}>
              <span>
                <IconButton onClick={handleAssistantClick} disabled={isCreatingReminder} size="small" sx={{ color: 'primary.main' }}>
                  {isCreatingReminder ? <CircularProgress size={14} /> : <MillosLogo size={20} agent />}
                </IconButton>
              </span>
            </Tooltip>
            {onDelete && (
              <Tooltip title="Delete certificate">
                <IconButton onClick={(e) => { e.stopPropagation(); setDeleteConfirmOpen(true); }} size="small" sx={{ color: 'error.main' }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Menu
          anchorEl={categoryMenuAnchor}
          open={Boolean(categoryMenuAnchor)}
          onClose={() => setCategoryMenuAnchor(null)}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                minWidth: 200,
                borderRadius: '10px',
                border: `1px solid ${palette.tokens.hairline}`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              },
            },
            list: { sx: { py: 0.5 } },
          }}
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <MenuItem
              key={opt}
              selected={opt === localCert.certificate_type_category}
              onClick={() => { setCategoryMenuAnchor(null); saveField('certificate_type_category', opt); }}
              sx={{
                fontSize: '0.85rem',
                py: 0.75,
                px: 2,
                '&.Mui-selected': { bgcolor: 'rgba(204,120,92,0.12)' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(204,120,92,0.18)' },
                '&:hover': { bgcolor: palette.tokens.canvas },
              }}
            >
              {opt}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Dates + ring — side by side (not stacked), click-to-edit inline. Action icons moved up
          next to the title, so this column is purely date info now. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {editingDateField === 'issue_date' ? (
            <TextField
              autoFocus
              type="date"
              size="small"
              variant="standard"
              value={dateDraft}
              onChange={(e) => setDateDraft(e.target.value)}
              onBlur={commitDateEdit}
              onKeyDown={(e) => { if (e.key === 'Enter') commitDateEdit(); if (e.key === 'Escape') setEditingDateField(null); }}
              onClick={(e) => e.stopPropagation()}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.72rem', p: 0 } }}
            />
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              onClick={(e) => { e.stopPropagation(); startEditDate('issue_date'); }}
              sx={{ fontSize: '0.72rem', cursor: 'pointer', '&:hover': { textDecoration: 'underline', textDecorationStyle: 'dotted' } }}
            >
              Issued {localCert.issue_date ? formatDate(localCert.issue_date) : 'Add date'}
            </Typography>
          )}

          {editingDateField === 'expiry_date' ? (
            <TextField
              autoFocus
              type="date"
              size="small"
              variant="standard"
              value={dateDraft}
              onChange={(e) => setDateDraft(e.target.value)}
              onBlur={commitDateEdit}
              onKeyDown={(e) => { if (e.key === 'Enter') commitDateEdit(); if (e.key === 'Escape') setEditingDateField(null); }}
              onClick={(e) => e.stopPropagation()}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem', fontWeight: 600, p: 0 } }}
            />
          ) : (
            <Typography
              variant="body2"
              fontWeight={600}
              onClick={(e) => { e.stopPropagation(); startEditDate('expiry_date'); }}
              sx={{ fontSize: '0.82rem', cursor: 'pointer', '&:hover': { textDecoration: 'underline', textDecorationStyle: 'dotted' } }}
            >
              {expiryStatus.severity === 'error' ? 'Expired' : 'Expires'} {formatDate(localCert.expiry_date)}
            </Typography>
          )}
        </Box>

        <ExpiryRing issueDate={localCert.issue_date} expiryDate={localCert.expiry_date} size={enlarged ? 68 : 56} />
      </Box>

      {/* Reminder Options Menu (unused directly here — kept for parity, ReminderEditDialog drives creation) */}
      <Menu anchorEl={reminderMenuAnchor} open={Boolean(reminderMenuAnchor)} onClose={() => setReminderMenuAnchor(null)}>
        {reminderOptions.map((option) => (
          <MenuItem
            key={option.days}
            onClick={() => createReminder(option.days)}
            disabled={reminderStatus?.active_reminders?.some(r => r.reminder_days_before === option.days)}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>

      <ReminderEditDialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        certificate={localCert}
        onSave={handleReminderSave}
        onDelete={handleDeleteReminder}
        existingReminder={reminderStatus?.active_reminders?.[0] ? (() => {
          const reminder = reminderStatus.active_reminders[0];
          const reminderDateTime = new Date(reminder.reminder_date);
          const hours = reminderDateTime.getHours().toString().padStart(2, '0');
          const minutes = reminderDateTime.getMinutes().toString().padStart(2, '0');
          return {
            text: `${localCert.certificate_type} certificate expires on ${localCert.expiry_date}`,
            daysBeforeExpiry: reminder.reminder_days_before || 7,
            reminderId: reminder.id,
            reminderTime: `${hours}:${minutes}`,
            reminderDate: reminderDateTime.toISOString().split('T')[0],
          };
        })() : undefined}
      />

      <EmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        certificate={localCert}
        onEmailSent={onReminderSuccess}
        onError={onReminderError}
      />

      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Version History - {localCert.certificate_type}</DialogTitle>
        <DialogContent>
          {loadingVersions ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : versionError ? (
            <Alert severity="error">{versionError}</Alert>
          ) : versions.length === 0 ? (
            <Alert severity="info">No previous versions found</Alert>
          ) : (
            <Box>
              {versions.map((version, index) => (
                <Box key={version.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Typography variant="subtitle2" gutterBottom>
                        Version {version.version_number} {index === 0 && <Chip label="Previous" size="small" color="primary" sx={{ ml: 1 }} />}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {version.property_address}
                      </Typography>
                      <Box display="flex" gap={2} mt={1}>
                        <Typography variant="caption"><strong>Issue:</strong> {version.issue_date || 'N/A'}</Typography>
                        <Typography variant="caption"><strong>Expiry:</strong> {version.expiry_date || 'N/A'}</Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                        Replaced by: {version.replaced_by_provider || 'Manual'} • {new Date(version.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small" startIcon={<RestoreIcon />} onClick={() => handleRevertToVersion(version.id)}>
                      Revert
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Certificate?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this certificate? This action cannot be undone.</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            <strong>{localCert.certificate_type}</strong><br />
            {certificate.property_address}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={() => { setDeleteConfirmOpen(false); if (onDelete) onDelete(certificate.id); }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CertificateListRow;
